/** Global notification provider (UX review F1/F4).
 *
 * Single toast/snackbar system for the app so pages stop re-implementing
 * local Snackbar state and error catches stop failing silently.
 *
 * Usage:
 *   const notify = useNotify();
 *   notify("Saved");                       // success
 *   notify("Failed to load", "error");     // error
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  SyntheticEvent,
} from "react";
import { Alert, AlertColor, Snackbar } from "@mui/material";

export interface NotificationMessage {
  id: number;
  message: string;
  severity: AlertColor;
}

interface NotifyContextValue {
  /** Show a toast. Returns nothing; auto-dismisses after 6s (errors 8s). */
  notify: (message: string, severity?: AlertColor) => void;
}

const NotifyContext = createContext<NotifyContextValue>({ notify: () => {} });

/** Provider that renders the stacked snackbars. */
export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<NotificationMessage[]>([]);
  const nextId = useMemo(() => ({ id: 1 }), []);

  const notify = useCallback(
    (message: string, severity: AlertColor = "success") => {
      const id = nextId.id++;
      setMessages((prev) => [...prev.slice(-3), { id, message, severity }]);
    },
    [nextId]
  );

  const dismiss = useCallback((id: number) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotifyContext.Provider value={value}>
      {children}
      {messages.map((m, index) => (
        <Snackbar
          key={m.id}
          open
          autoHideDuration={m.severity === "error" ? 8000 : 6000}
          onClose={(_e: React.SyntheticEvent | Event, reason?: string) => {
            if (reason === "clickaway") return;
            dismiss(m.id);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ bottom: (index + 1) * 72 }} // stack without overlapping
        >
          <Alert
            onClose={() => dismiss(m.id)}
            severity={m.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {m.message}
          </Alert>
        </Snackbar>
      ))}
    </NotifyContext.Provider>
  );
}

/** Access the global notify function. */
export function useNotify(): (message: string, severity?: AlertColor) => void {
  return useContext(NotifyContext).notify;
}
